import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles, Utensils, Home as HomeIcon, HeartHandshake, Eye, LayoutTemplate, X, History, RotateCcw, ChevronDown, ChevronUp, Copy } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { BRAND_COLORS, STATUS_COLORS, withAlpha } from "../lib/bookingStatus";
import { deDateMediumTime } from "../lib/dateFormat";

/**
 * Feb 2026 · Kunden-Präferenzen (Allergien / Ernährung / Home-Setup).
 *
 * Standard-Tags + Freitext. Persistiert am User-Dokument. Änderungen lösen
 * eine gedrosselte Notification (max. 1/h) an Admin + zugewiesene Concierges
 * aus (Backend: `customer_preferences.py`).
 */

// Kuratierte Standard-Listen. Werden im UI als Chips angezeigt, plus
// Freitext für individuelle Ergänzungen.
const TAGS_ALLERGIES_DE = [
  "Nüsse", "Milch", "Gluten", "Eier", "Fisch", "Schalentiere", "Sesam", "Soja",
];
const TAGS_ALLERGIES_EN = [
  "Nuts", "Dairy", "Gluten", "Eggs", "Fish", "Shellfish", "Sesame", "Soy",
];
const TAGS_FOOD_DE = [
  "Vegetarisch", "Vegan", "Halal", "Koscher", "Zuckerarm", "Laktosefrei",
];
const TAGS_FOOD_EN = [
  "Vegetarian", "Vegan", "Halal", "Kosher", "Low sugar", "Lactose-free",
];
const TAGS_HOME_DE = [
  "Frische Blumen", "Kaminholz auffüllen", "Einkauf nach Liste", "Pralinen",
  "Champagner kühlstellen", "Post bereitlegen", "Zimmer kühl 18 Grad", "Zimmer warm 22 Grad",
];
const TAGS_HOME_EN = [
  "Fresh flowers", "Refill firewood", "Groceries per list", "Chocolates",
  "Chill champagne", "Lay out mail", "Rooms cool 18°C", "Rooms warm 22°C",
];

function normArray(v) {
  return Array.isArray(v) ? v.filter(Boolean) : [];
}

function mergeUnique(a, b) {
  const set = new Set([...normArray(a), ...normArray(b)]);
  return [...set];
}

function equalArrays(a, b) {
  const A = [...normArray(a)].sort();
  const B = [...normArray(b)].sort();
  if (A.length !== B.length) return false;
  return A.every((x, i) => x === B[i]);
}

function TemplatesDialog({ de, templates, loading, onApply, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: `${withAlpha(BRAND_COLORS.navyDark, 0.75)}` }}
      onClick={onClose}
      data-testid="prefs-templates-dialog"
    >
      <div className="bg-ivory max-w-2xl w-full border border-gold/40 p-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="label-caps mb-1" style={{ color: BRAND_COLORS.gold }}>Weissblom</div>
            <h3 className="font-display text-2xl text-navy">{de ? "Vorlagen anwenden" : "Apply a template"}</h3>
            <p className="text-[13px] text-navy-dark/70 leading-relaxed mt-1">
              {de
                ? "Jede Vorlage ergänzt Ihre bestehenden Chips. Sie können danach beliebig verfeinern und speichern."
                : "Each template extends your existing chips. You can refine and save afterwards."}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="prefs-templates-close"
            aria-label={de ? "Schließen" : "Close"}
            className="p-1 text-navy-dark/50 hover:text-navy"
          >
            <X className="w-4 h-4" strokeWidth={2} />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-navy-dark/60 text-[13px]">
            <Loader2 className="w-4 h-4 animate-spin" /> {de ? "Wird geladen …" : "Loading …"}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {templates.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => onApply(t)}
                data-testid={`prefs-template-${t.id}`}
                className="text-left bg-white border border-navy/15 hover:border-gold hover:shadow-sm transition-all p-4"
              >
                <div className="font-display text-[17px] text-navy leading-tight mb-1">
                  {de ? t.label_de : t.label_en}
                </div>
                <p className="text-[12px] text-navy-dark/70 leading-snug mb-3">
                  {de ? t.description_de : t.description_en}
                </p>
                <div className="flex flex-wrap gap-1">
                  {[
                    ...(t.prefs?.allergies || []),
                    ...(t.prefs?.food || []),
                    ...(t.prefs?.home || []),
                  ].slice(0, 5).map((tag, i) => (
                    <span key={`${tag}-${i}`} className="text-[10.5px] tracking-wide px-2 py-0.5 bg-ivory/70 border border-navy/15 text-navy-dark/85">
                      {tag}
                    </span>
                  ))}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


function TagPicker({ label, description, icon: Icon, tags, value, onChange, testidBase, disabled, customEntries = null, onAddCustom = null, onDeleteCustom = null, exclusiveGroups = null }) {
  const [custom, setCustom] = useState("");
  const [menuFor, setMenuFor] = useState(null);
  const lpTimer = useRef(null);
  const set = new Set(normArray(value));
  const toggle = (t) => {
    if (disabled) return;
    const cur = normArray(value);
    if (cur.includes(t)) {
      onChange(cur.filter((x) => x !== t));
      return;
    }
    // Gegenseitiger Ausschluss: andere Einträge derselben Gruppe still abwählen.
    const group = (exclusiveGroups || []).find((g) => g.includes(t));
    const base = group ? cur.filter((x) => !group.includes(x)) : cur;
    onChange([...base, t]);
  };
  const addCustom = () => {
    const clean = custom.trim();
    if (!clean) return;
    if (onAddCustom) { onAddCustom(clean); setCustom(""); return; }
    if (set.has(clean)) { setCustom(""); return; }
    onChange([...normArray(value), clean]);
    setCustom("");
  };
  // Quelle der eigenen Kacheln: explizite Liste (Zuhause) oder Legacy (Wert ∖ Vorgaben).
  const customChips = customEntries != null
    ? normArray(customEntries)
    : normArray(value).filter((v) => !tags.includes(v));
  const startLongPress = (label) => {
    if (!onDeleteCustom || disabled) return;
    if (lpTimer.current) clearTimeout(lpTimer.current);
    lpTimer.current = setTimeout(() => setMenuFor(label), 500);
  };
  const cancelLongPress = () => { if (lpTimer.current) { clearTimeout(lpTimer.current); lpTimer.current = null; } };
  return (
    <div data-testid={`${testidBase}-block`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.6} />
        <div className="label-caps" style={{ color: BRAND_COLORS.gold }}>{label}</div>
      </div>
      {description && (
        <p className="text-[12.5px] text-navy-dark/60 italic leading-relaxed mb-3">{description}</p>
      )}
      <div className="flex flex-wrap gap-2" data-testid={`${testidBase}-chips`}>
        {tags.map((t, i) => {
          const active = set.has(t);
          return (
            <button
              key={`${t}-${i}`}
              type="button"
              disabled={disabled}
              onClick={() => toggle(t)}
              data-testid={`${testidBase}-chip-${i}`}
              className={`text-[12px] tracking-wide px-3 py-1.5 border transition-colors ${
                active
                  ? "bg-navy text-ivory border-navy"
                  : "bg-white text-navy-dark/85 border-navy/20 hover:border-gold hover:text-navy"
              } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
              aria-pressed={active}
            >
              {t}
            </button>
          );
        })}
        {/* Eigene Kacheln: an-/abwählbar wie Vorgaben (Kurz-Tipp/Klick);
            löschbar NUR via Langdruck bzw. Rechtsklick. Quelle: customChips
            (persistente eigene Einträge), nicht nur die aktuelle Auswahl. */}
        {customChips.map((v, i) => {
          const active = set.has(v);
          const deletable = !!onDeleteCustom;
          return (
            <button
              key={`custom-${v}-${i}`}
              type="button"
              disabled={disabled}
              onClick={() => toggle(v)}
              onContextMenu={deletable ? (e) => { e.preventDefault(); if (!disabled) setMenuFor(v); } : undefined}
              onTouchStart={deletable ? () => startLongPress(v) : undefined}
              onTouchEnd={deletable ? cancelLongPress : undefined}
              onTouchMove={deletable ? cancelLongPress : undefined}
              data-testid={`${testidBase}-custom-${i}`}
              className={`text-[12px] tracking-wide px-3 py-1.5 border transition-colors ${
                active
                  ? "bg-navy text-ivory border-navy"
                  : "bg-white text-navy-dark/85 border-navy/20 hover:border-gold hover:text-navy"
              } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
              aria-pressed={active}
              title={deletable ? "Antippen zum Aus-/Abwählen · lange drücken oder Rechtsklick zum Löschen" : undefined}
            >
              {v}
            </button>
          );
        })}
      </div>
      <div className="mt-3 flex items-center gap-2">
        <input
          data-testid={`${testidBase}-custom-input`}
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustom(); } }}
          disabled={disabled}
          placeholder="+ Eigener Eintrag"
          className="flex-1 bg-ivory border border-navy/15 focus:border-gold focus:outline-none px-3 py-2 text-[12.5px] text-navy-dark"
        />
        <button
          type="button"
          onClick={addCustom}
          disabled={disabled || !custom.trim()}
          data-testid={`${testidBase}-custom-add`}
          className="text-[11px] tracking-widest uppercase text-navy-dark/70 hover:text-navy px-3 py-2 disabled:opacity-30"
        >
          Hinzufügen
        </button>
      </div>
      {menuFor != null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          data-testid={`${testidBase}-delete-modal`}
          onClick={() => setMenuFor(null)}
        >
          <div className="absolute inset-0 bg-navy/40" />
          <div className="relative bg-white border border-navy/15 shadow-lg max-w-xs w-full p-5" onClick={(e) => e.stopPropagation()}>
            <div className="text-[14px] text-navy mb-1">Eigenen Eintrag löschen?</div>
            <div className="text-[13px] text-navy-dark/70 mb-4 break-words">„{menuFor}"</div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setMenuFor(null)}
                data-testid={`${testidBase}-delete-cancel`}
                className="text-[12px] tracking-wide px-3 py-1.5 border border-navy/20 text-navy-dark/80 hover:border-navy"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={() => { onDeleteCustom?.(menuFor); setMenuFor(null); }}
                data-testid={`${testidBase}-delete-confirm`}
                className="text-[12px] tracking-wide px-3 py-1.5 border text-ivory"
                style={{ background: STATUS_COLORS.red, borderColor: STATUS_COLORS.red }}
              >
                Löschen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function CustomerPreferencesSection({ propertyId, propertyName, properties }) {
  const { user } = useAuth();
  const { lang } = useI18n();
  // Feb 2026 · 9 Sep 2026 · Präferenzen-Umzug: Der Parent (CustomerSettings)
  // reicht `properties` aus dem PropertyContext durch. Wo die Section ohne
  // Objekt-Liste eingebettet ist (BookingPrep etc.), bleibt der Button
  // still ausgeblendet.
  const de = lang !== "en";
  const isFamily = user?.role === "family";
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [orig, setOrig] = useState({ allergies: [], food: [], home: [], home_custom: [], notes: "" });
  const [prefs, setPrefs] = useState({ allergies: [], food: [], home: [], home_custom: [], notes: "" });
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  // Feb 2026 · Historie + Ein-Klick-Wiederherstellen
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [restoringAt, setRestoringAt] = useState(null);
  const [confirmSnap, setConfirmSnap] = useState(null); // holds a snapshot pending confirm

  // Feb 2026 · 8 Sep 2026 · User-Directive „Präferenzen pro Objekt":
  // Wenn `propertyId` durchgereicht wird, hängen wir sie als Query-Param
  // an alle Präferenz-Endpoints. Backend fällt auf Legacy-Datensatz zurück,
  // wenn für dieses Objekt noch keine eigenen Prefs existieren.
  const qs = propertyId ? `?property_id=${encodeURIComponent(propertyId)}` : "";

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get("/me/customer-preferences/history");
      setHistory(data?.history || []);
    } catch (e) {
      console.debug("history load skipped:", e?.response?.status);
    }
  }, []);

  const loadPrefs = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data }, ] = await Promise.all([
        api.get(`/me/customer-preferences${qs}`),
        loadHistory(),
      ]);
      const clean = {
        allergies: normArray(data.allergies),
        food: normArray(data.food),
        home: normArray(data.home),
        home_custom: normArray(data.home_custom),
        notes: (data.notes || ""),
      };
      setOrig(clean); setPrefs(clean);
    } catch (e) {
      console.debug("preferences load skipped:", e?.response?.status);
    } finally { setLoading(false); }
  }, [qs, loadHistory]);

  const openTemplates = async () => {
    if (isFamily) return;
    setTemplatesOpen(true);
    if (templates.length === 0) {
      setTemplatesLoading(true);
      try {
        const { data } = await api.get("/me/customer-preferences/templates");
        setTemplates(data?.templates || []);
      } catch (e) {
        toast.error(formatErr(e.response?.data?.detail) || e.message);
      } finally { setTemplatesLoading(false); }
    }
  };

  const applyTemplate = (tpl) => {
    if (!tpl?.prefs) return;
    setPrefs((cur) => ({
      allergies: mergeUnique(cur.allergies, tpl.prefs.allergies || []),
      food:      mergeUnique(cur.food, tpl.prefs.food || []),
      home:      mergeUnique(cur.home, tpl.prefs.home || []),
      home_custom: cur.home_custom || [],
      notes:     cur.notes || tpl.prefs.notes || "",
    }));
    setTemplatesOpen(false);
    toast.success(de
      ? `Vorlage „${tpl.label_de}" hinzugefügt · noch nicht gespeichert.`
      : `Template "${tpl.label_en}" added · not saved yet.`);
  };

  const doRestore = async (snap) => {
    if (isFamily || !snap?.replaced_at) return;
    setRestoringAt(snap.replaced_at);
    try {
      const { data } = await api.post("/me/customer-preferences/restore", {
        replaced_at: snap.replaced_at,
      });
      const clean = {
        allergies: normArray(data.allergies),
        food: normArray(data.food),
        home: normArray(data.home),
        home_custom: normArray(data.home_custom),
        notes: (data.notes || ""),
      };
      setOrig(clean); setPrefs(clean);
      await loadHistory();
      toast.success(de
        ? "Version wiederhergestellt · Ihr Concierge-Team wurde benachrichtigt."
        : "Version restored · your concierge team has been notified.");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setRestoringAt(null); setConfirmSnap(null); }
  };

  // Feb 2026 · 9 Sep 2026 · Präferenzen-Umzug (Copy-Modal).
  const [copyOpen, setCopyOpen] = useState(false);
  const [copySource, setCopySource] = useState("");
  const [copyBusy, setCopyBusy] = useState(false);
  // Andere Objekte des Kunden (ohne das aktuelle Ziel) — Quelle für Kopie.
  const otherProperties = useMemo(() => {
    return (properties || []).filter((p) => p?.id && p.id !== propertyId);
  }, [properties, propertyId]);
  const canCopyFromOther = !isFamily && !!propertyId && otherProperties.length > 0;

  const openCopyDialog = () => {
    if (!canCopyFromOther) return;
    setCopySource(otherProperties[0]?.id || "");
    setCopyOpen(true);
  };
  const runCopy = async () => {
    if (!copySource || !propertyId || copySource === propertyId) return;
    setCopyBusy(true);
    try {
      const { data } = await api.post("/me/customer-preferences/copy", {
        from_property_id: copySource,
        to_property_id: propertyId,
      });
      const clean = {
        allergies: normArray(data.allergies),
        food: normArray(data.food),
        home: normArray(data.home),
        home_custom: normArray(data.home_custom),
        notes: (data.notes || ""),
      };
      setOrig(clean); setPrefs(clean);
      await loadHistory();
      setCopyOpen(false);
      const srcName = otherProperties.find((p) => p.id === copySource)?.name || "";
      toast.success(de
        ? `Präferenzen von „${srcName}" übernommen · Ihr Concierge-Team wurde benachrichtigt.`
        : `Preferences copied from "${srcName}" · your concierge team has been notified.`);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setCopyBusy(false); }
  };

  useEffect(() => {
    loadPrefs();
  }, [loadPrefs]);

  const dirty = useMemo(() => {
    return (
      !equalArrays(orig.allergies, prefs.allergies) ||
      !equalArrays(orig.food, prefs.food) ||
      !equalArrays(orig.home, prefs.home) ||
      !equalArrays(orig.home_custom, prefs.home_custom) ||
      (orig.notes || "") !== (prefs.notes || "")
    );
  }, [orig, prefs]);

  const save = async () => {
    if (isFamily) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/me/customer-preferences${qs}`, prefs);
      const clean = {
        allergies: normArray(data.allergies),
        food: normArray(data.food),
        home: normArray(data.home),
        home_custom: normArray(data.home_custom),
        notes: (data.notes || ""),
      };
      setOrig(clean); setPrefs(clean);
      await loadHistory();
      toast.success(de
        ? "Präferenzen gespeichert · Ihr Concierge-Team wurde benachrichtigt."
        : "Preferences saved · your concierge team has been notified.");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  // Zuhause: 8 feste Vorgaben (nur an-/abwählbar) + eigene, löschbare Einträge.
  // Eigene Einträge persistieren pro Objekt in `home_custom`, unabhängig von der
  // Auswahl; bereits gewählte Nicht-Vorgabe-Werte (Alt-Daten) erscheinen ebenfalls
  // als eigene (löschbare) Kacheln — es gehen keine Daten verloren.
  const HOME_PRESETS = de ? TAGS_HOME_DE : TAGS_HOME_EN;
  const HOME_PRESET_SET = useMemo(() => new Set([...TAGS_HOME_DE, ...TAGS_HOME_EN]), []);
  const homeCustomEntries = useMemo(() => {
    const fromCustom = normArray(prefs.home_custom);
    const orphans = normArray(prefs.home).filter((v) => !HOME_PRESET_SET.has(v));
    return [...new Set([...fromCustom, ...orphans])];
  }, [prefs.home_custom, prefs.home, HOME_PRESET_SET]);
  const addHomeCustom = (label) => {
    const clean = (label || "").trim();
    if (!clean) return;
    const preset = HOME_PRESETS.find((p) => p.toLowerCase() === clean.toLowerCase());
    if (preset) {
      setPrefs((cur) => ({ ...cur, home: cur.home.includes(preset) ? cur.home : [...cur.home, preset] }));
      return;
    }
    setPrefs((cur) => {
      const customList = normArray(cur.home_custom);
      const existing = customList.find((c) => c.toLowerCase() === clean.toLowerCase());
      const useLabel = existing || clean;
      const nextCustom = existing ? customList : [...customList, clean].slice(0, 20);
      const nextHome = cur.home.includes(useLabel) ? cur.home : [...cur.home, useLabel];
      return { ...cur, home_custom: nextCustom, home: nextHome };
    });
  };
  const deleteHomeCustom = (label) => {
    setPrefs((cur) => ({
      ...cur,
      home_custom: normArray(cur.home_custom).filter((c) => c !== label),
      home: normArray(cur.home).filter((h) => h !== label),
    }));
  };

  return (
    <section className="bg-white border border-navy/10 p-6 md:p-8 space-y-8" data-testid="customer-preferences-section">
      <div>
        <div className="flex items-center gap-2 mb-2">
          <HeartHandshake className="w-5 h-5" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.4} />
          <h2 className="font-display text-2xl text-navy">
            {de ? "Voreingestellte Präferenzen" : "Preset preferences"}
            {propertyName && (
              <span className="ml-2 text-[13px] tracking-wider uppercase text-navy-dark/50 not-italic">
                · {propertyName}
              </span>
            )}
          </h2>
          {isFamily && (
            <span
              className="ml-2 inline-flex items-center gap-1 text-[10px] tracking-[0.18em] uppercase px-2 py-0.5 border"
              style={{ background: withAlpha(BRAND_COLORS.gold, 0.12), color: BRAND_COLORS.goldDark || "#8A7635", borderColor: BRAND_COLORS.gold }}
              data-testid="prefs-family-readonly-badge"
            >
              <Eye className="w-3 h-3" strokeWidth={2} /> {de ? "Nur Ansicht" : "View only"}
            </span>
          )}
          {!isFamily && canCopyFromOther && (
            <button
              type="button"
              onClick={openCopyDialog}
              data-testid="prefs-copy-btn"
              className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.15em] uppercase text-navy-dark/70 hover:text-navy border border-navy/15 hover:border-navy/40 transition-colors px-3 py-1.5"
              title={de ? "Von anderem Objekt kopieren" : "Copy from another property"}
            >
              <Copy className="w-3.5 h-3.5" strokeWidth={1.6} />
              {de ? "Übernehmen" : "Copy"}
            </button>
          )}
          {!isFamily && (
            <button
              type="button"
              onClick={openTemplates}
              data-testid="prefs-templates-btn"
              className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.15em] uppercase text-navy-dark/70 hover:text-navy border border-navy/15 hover:border-navy/40 transition-colors px-3 py-1.5"
              title={de ? "Vorlage öffnen" : "Open templates"}
            >
              <LayoutTemplate className="w-3.5 h-3.5" strokeWidth={1.6} />
              {de ? "Vorlagen" : "Templates"}
            </button>
          )}
        </div>
        {!isFamily && (
          <p className="text-[13px] text-navy leading-relaxed mb-1 font-semibold" data-testid="prefs-intro-line">
            {de ? "Damit wir Sie besser kennenlernen." : "So we get to know you better."}
          </p>
        )}
        <p className="text-[13px] text-navy-dark/70 leading-relaxed">
          {isFamily
            ? (de
                ? "Diese Präferenzen des Haushalts sind für Sie sichtbar. Nur der Eigentümer kann sie ändern."
                : "These household preferences are visible to you. Only the owner can change them.")
            : (de
                ? "Damit unser Team bei jeder Vorbereitung passgenau arbeitet, erzählen Sie uns etwas über Ihre Vorlieben und Gewohnheiten, Ihr Home-Setup und eventuelle Allergien."
                : "So your concierge team can prepare with precision every time — allergies, food and home setup in one place.")}
        </p>
      </div>

      {templatesOpen && (
        <TemplatesDialog
          de={de}
          templates={templates}
          loading={templatesLoading}
          onApply={applyTemplate}
          onClose={() => setTemplatesOpen(false)}
        />
      )}

      {/* Feb 2026 · 9 Sep 2026 · Präferenzen-Umzug (Copy-Dialog). */}
      {copyOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: withAlpha(BRAND_COLORS.navyDark, 0.75) }}
          onClick={() => setCopyOpen(false)}
          data-testid="prefs-copy-dialog"
        >
          <div
            role="dialog"
            aria-modal="true"
            className="bg-white w-full max-w-md relative p-6 md:p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setCopyOpen(false)}
              className="absolute top-3 right-3 text-navy-dark/50 hover:text-navy"
              data-testid="prefs-copy-close"
              aria-label={de ? "Schliessen" : "Close"}
            >
              <X className="w-4 h-4" strokeWidth={1.6} />
            </button>
            <div className="flex items-center gap-2 mb-1">
              <Copy className="w-5 h-5" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.4} />
              <h2 className="font-display text-2xl text-navy leading-tight">
                {de ? "Präferenzen übernehmen" : "Copy preferences"}
              </h2>
            </div>
            <p className="text-[13px] text-navy-dark/70 leading-relaxed mb-4">
              {de
                ? `Wählen Sie ein Objekt aus, dessen Präferenzen Sie für „${propertyName || "dieses Objekt"}" übernehmen möchten.`
                : `Pick a property whose preferences you want to copy to "${propertyName || "this property"}".`}
            </p>
            <label className="label-caps block mb-1.5">
              {de ? "Quelle" : "Source"}
            </label>
            <select
              data-testid="prefs-copy-source"
              value={copySource}
              onChange={(e) => setCopySource(e.target.value)}
              className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-2.5 text-[14px] text-navy mb-2"
            >
              {otherProperties.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
            <p className="text-[11.5px] text-navy-dark/55 italic mb-5">
              {de
                ? "Ihre aktuellen Präferenzen für dieses Objekt werden ersetzt. Die vorige Version bleibt in der Historie zum Wiederherstellen."
                : "Your current preferences for this property will be replaced. The previous version stays in history so you can restore it anytime."}
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setCopyOpen(false)}
                className="text-[11px] tracking-widest uppercase text-navy-dark/70 hover:text-navy px-3 py-2"
                data-testid="prefs-copy-cancel"
              >
                {de ? "Abbrechen" : "Cancel"}
              </button>
              <button
                type="button"
                onClick={runCopy}
                disabled={copyBusy || !copySource}
                data-testid="prefs-copy-confirm"
                className="inline-flex items-center gap-2 text-ivory px-5 py-2.5 text-[11px] tracking-[0.18em] uppercase hover:opacity-90 disabled:opacity-60"
                style={{ background: BRAND_COLORS.navy }}
              >
                {copyBusy && <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.8} />}
                {de ? "Übernehmen" : "Copy"}
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-navy-dark/60 text-[13px]">
          <Loader2 className="w-4 h-4 animate-spin" /> {de ? "Wird geladen …" : "Loading …"}
        </div>
      ) : (
        <>
          <TagPicker
            testidBase="prefs-home"
            label={de ? "Zuhause" : "Home setup"}
            description={de
              ? "Wünsche für die Ankunft — antippen zum Aus-/Abwählen. Eigene Wünsche unten ergänzen (Langdruck oder Rechtsklick löscht einen eigenen Eintrag)."
              : "Arrival wishes — tap to select or deselect. Add your own below (long-press or right-click to delete your own)."}
            icon={HomeIcon}
            tags={HOME_PRESETS}
            value={prefs.home}
            onChange={(v) => setPrefs({ ...prefs, home: v })}
            disabled={saving || isFamily}
            customEntries={homeCustomEntries}
            onAddCustom={addHomeCustom}
            onDeleteCustom={deleteHomeCustom}
            exclusiveGroups={[[HOME_PRESETS[6], HOME_PRESETS[7]]]}
          />
          <TagPicker
            testidBase="prefs-food"
            label={de ? "Ernährung" : "Food style"}
            description={de
              ? "Ihre Gewohnheiten für Einkauf und Speiseauswahl."
              : "Your habits for shopping and food choices."}
            icon={Utensils}
            tags={de ? TAGS_FOOD_DE : TAGS_FOOD_EN}
            value={prefs.food}
            onChange={(v) => setPrefs({ ...prefs, food: v })}
            disabled={saving || isFamily}
          />
          <TagPicker
            testidBase="prefs-allergies"
            label={de ? "Allergien" : "Allergies"}
            description={de
              ? "Was bei Speisen und Blumen unbedingt vermieden werden soll."
              : "What to strictly avoid in food and flowers."}
            icon={Sparkles}
            tags={de ? TAGS_ALLERGIES_DE : TAGS_ALLERGIES_EN}
            value={prefs.allergies}
            onChange={(v) => setPrefs({ ...prefs, allergies: v })}
            disabled={saving || isFamily}
          />

          <div>
            <div className="label-caps mb-2" style={{ color: BRAND_COLORS.gold }}>{de ? "Notiz" : "Notes"}</div>
            <textarea
              rows={3}
              data-testid="prefs-notes"
              value={prefs.notes}
              onChange={(e) => setPrefs({ ...prefs, notes: e.target.value })}
              disabled={saving || isFamily}
              placeholder={de
                ? "Individuelle Wünsche, Kontext oder Geschichten für Ihr Team …"
                : "Individual wishes, context or stories for your team …"}
              className="w-full bg-ivory border border-navy/15 focus:border-gold focus:outline-none px-3 py-2.5 text-[13px] text-navy-dark leading-relaxed disabled:opacity-70 disabled:cursor-not-allowed"
            />
          </div>

          {!isFamily && (
            <div className="flex items-center justify-between gap-3 pt-4 border-t border-navy/10 flex-wrap">
              <div className="text-[11.5px] text-navy-dark/60 italic">
                {de
                  ? "Änderungen werden Ihrem Concierge-Team gebündelt gemeldet (max. 1 Push pro Stunde)."
                  : "Changes are notified to your concierge team in a batched way (max 1 push per hour)."}
              </div>
              <div className="flex items-center gap-3">
                {dirty && (
                  <span className="text-[11px] tracking-wider uppercase" style={{ color: BRAND_COLORS.gold }}>
                    · {de ? "Ungespeicherte Änderungen" : "Unsaved changes"}
                  </span>
                )}
                <button
                  type="button"
                  onClick={save}
                  disabled={!dirty || saving}
                  data-testid="prefs-save-btn"
                  className="inline-flex items-center gap-2 text-ivory px-6 py-2.5 text-[11px] tracking-[0.18em] uppercase hover:opacity-90 disabled:opacity-40"
                  style={{ background: BRAND_COLORS.navy }}
                >
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                  {de ? "Speichern" : "Save"}
                </button>
              </div>
            </div>
          )}

          {/* Feb 2026 · Historie + Ein-Klick-Wiederherstellen (Owner only).
              Family sieht Präferenzen read-only, kann Historie einsehen, aber
              nicht restaurieren — der Restore-Button wird ausgeblendet. */}
          {history.length > 0 && (
            <div className="pt-5 border-t border-navy/10" data-testid="prefs-history-block">
              <button
                type="button"
                onClick={() => setHistoryOpen((v) => !v)}
                data-testid="prefs-history-toggle"
                className="w-full flex items-center justify-between gap-2 text-left group"
              >
                <span className="inline-flex items-center gap-2 text-navy-dark/70 group-hover:text-navy transition-colors">
                  <History className="w-4 h-4" strokeWidth={1.6} style={{ color: BRAND_COLORS.gold }} />
                  <span className="label-caps">
                    {de ? `Frühere Versionen (${history.length})` : `Previous versions (${history.length})`}
                  </span>
                </span>
                {historyOpen
                  ? <ChevronUp className="w-4 h-4 text-navy-dark/60" />
                  : <ChevronDown className="w-4 h-4 text-navy-dark/60" />}
              </button>

              {historyOpen && (
                <ol className="mt-4 space-y-4" data-testid="prefs-history-list">
                  {history.map((h, i) => {
                    const snap = h.snapshot || {};
                    const when = deDateMediumTime(h.replaced_at);
                    const isRestoring = restoringAt === h.replaced_at;
                    return (
                      <li
                        key={`${h.replaced_at}-${i}`}
                        className="pl-3 border-l-2 bg-ivory/40"
                        style={{ borderColor: withAlpha(BRAND_COLORS.gold, 0.45) }}
                        data-testid={`prefs-history-item-${i}`}
                      >
                        <div className="flex items-start justify-between gap-3 flex-wrap p-3">
                          <div className="min-w-0 flex-1">
                            <div className="label-caps mb-1 text-navy-dark/55">
                              {de ? "Version -" : "Version -"}{i + 1} · {when}
                            </div>
                            <HistorySnapshotPreview snap={snap} de={de} testid={`prefs-history-preview-${i}`} />
                          </div>
                          {!isFamily && (
                            <button
                              type="button"
                              onClick={() => setConfirmSnap(h)}
                              disabled={isRestoring || saving}
                              data-testid={`prefs-history-restore-${i}`}
                              title={de ? "Diese Version wiederherstellen" : "Restore this version"}
                              className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.15em] uppercase text-navy-dark/75 hover:text-navy border border-navy/15 hover:border-gold px-3 py-1.5 shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              {isRestoring
                                ? <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.6} />
                                : <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.6} />}
                              {de ? "Wiederherstellen" : "Restore"}
                            </button>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>
          )}
        </>
      )}

      {/* Restore-Bestätigungsdialog — bewusster Zwischenschritt, damit ein
          versehentlicher Klick nicht sofort die aktuellen Präferenzen
          überschreibt (auch wenn sie durch den Restore selbst wieder in die
          Historie wandern). */}
      {confirmSnap && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: withAlpha(BRAND_COLORS.navyDark, 0.75) }}
          onClick={() => (restoringAt ? null : setConfirmSnap(null))}
          data-testid="prefs-restore-confirm-dialog"
        >
          <div
            className="bg-ivory max-w-lg w-full border border-gold/40 p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="label-caps mb-1" style={{ color: BRAND_COLORS.gold }}>Weissblom</div>
            <h3 className="font-display text-2xl text-navy mb-3">
              {de ? "Version wiederherstellen?" : "Restore this version?"}
            </h3>
            <p className="text-[13px] text-navy-dark/75 leading-relaxed mb-5">
              {de
                ? "Die aktuellen Präferenzen wandern in die Historie und diese Version wird wieder aktiv. Ihr Concierge-Team wird informiert."
                : "Your current preferences will move into history and this version becomes active again. Your concierge team will be informed."}
            </p>
            <div
              className="border border-navy/10 bg-white p-3 mb-6 max-h-48 overflow-y-auto"
              data-testid="prefs-restore-preview"
            >
              <HistorySnapshotPreview snap={confirmSnap.snapshot || {}} de={de} testid="prefs-restore-preview-inner" />
            </div>
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setConfirmSnap(null)}
                disabled={!!restoringAt}
                data-testid="prefs-restore-cancel"
                className="text-[11px] tracking-[0.18em] uppercase text-navy-dark/70 hover:text-navy px-4 py-2 disabled:opacity-40"
              >
                {de ? "Abbrechen" : "Cancel"}
              </button>
              <button
                type="button"
                onClick={() => doRestore(confirmSnap)}
                disabled={!!restoringAt}
                data-testid="prefs-restore-confirm"
                className="inline-flex items-center gap-2 text-ivory px-6 py-2.5 text-[11px] tracking-[0.18em] uppercase hover:opacity-90 disabled:opacity-40"
                style={{ background: BRAND_COLORS.navy }}
              >
                {restoringAt ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                {de ? "Wiederherstellen" : "Restore"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

// Kompakte Snapshot-Preview für Historie-Einträge + Bestätigungsdialog.
function HistorySnapshotPreview({ snap, de, testid }) {
  if (!snap) return null;
  const groups = [
    { label: de ? "Allergien" : "Allergies", arr: snap.allergies },
    { label: de ? "Ernährung" : "Food",      arr: snap.food },
    { label: de ? "Zuhause" : "Home",        arr: snap.home },
  ].filter((g) => (g.arr || []).length > 0);
  const notes = (snap.notes || "").trim();
  if (groups.length === 0 && !notes) {
    return <div className="text-[12px] text-navy-dark/50 italic">— {de ? "leere Version" : "empty version"} —</div>;
  }
  return (
    <div className="space-y-1.5" data-testid={testid}>
      {groups.map((g) => (
        <div key={g.label} className="text-[12px] text-navy-dark/85 leading-snug">
          <span className="label-caps text-navy-dark/50 mr-2">{g.label}</span>
          <span>{g.arr.join(", ")}</span>
        </div>
      ))}
      {notes && (
        <div className="text-[12px] text-navy-dark/75 italic leading-snug">
          <span className="label-caps not-italic text-navy-dark/50 mr-2">
            {de ? "Notiz" : "Note"}
          </span>
          {notes}
        </div>
      )}
    </div>
  );
}
